import React, { useEffect, useState } from "react";
import { Formik, Form, FastField } from "formik";
import * as Yup from "yup";
import {
  Button,
  Grid,
  Typography,
  Box,
  CircularProgress,
  TextField,
} from "@mui/material";
import dayjs from "dayjs";
import { useNavigate } from "react-router-dom";
import {
  createExport,
  getExportById,
  updateExport,
} from "../../api/exports";
import { getClientList } from "../../api/client";
import { useSnack } from "../../services/SnakbarProvider";
import { handleError } from "../../services/errorHandler";
import { getCatalogue, getStatusCatalogue } from "../../api/common/catalogues.ts";
import DropdownSelector from "../../atoms/Common/DropdownSelector";
import MyDatePicker from "../../atoms/Common/MyDatePicker";
import TariffFractionForm from "./TariffFractionForm";
import LabeledCheckbox from "../../atoms/Inputs/LabeledCheckbox.tsx";
import CurrencyFieldText from "../../atoms/Inputs/CurrencyField";
import { LGNumber } from "../../atoms/Inputs/LGNumber.tsx";

// Header-only form: creates/edits the Export shipment itself. Items are managed
// separately, on the granular detail page (/exports/items) this form navigates to
// after a successful save — see Frontend/src/screens/Exports/ExportDetailScreen.js.
const validationSchema = Yup.object().shape({
  export: Yup.object().shape({
    consigneeId: Yup.number()
      .typeError("Seleccione un cliente")
      .required("Seleccione un cliente"),
    statusId: Yup.number().nullable(),
    forwarderId: Yup.number()
      .typeError("Seleccione un forwarder")
      .required("Seleccione un forwarder"),
    packingListDate: Yup.date()
      .typeError("Fecha de packing list inválida")
      .nullable(),
    agentDeliveryDate: Yup.date()
      .typeError("Fecha de entrega inválida")
      .nullable(),
    requestNo: Yup.string(),
    hasLock: Yup.boolean(),
    lockNo: Yup.string().nullable(),
    containerTypeId: Yup.number()
      .typeError("Seleccione un tipo de contenedor")
      .nullable(),
    shipmentTypeId: Yup.number()
      .typeError("Seleccione un tipo de embarque")
      .nullable(),
    incotermId: Yup.number()
      .typeError("Seleccione un Incoterm")
      .nullable(),
    invoiceNo: Yup.number()
      .typeError("Número de factura es obligatorio")
      .required("Número de factura es obligatorio"),
    invoiceDate: Yup.date()
      .typeError("Fecha de factura inválida")
      .nullable(),
    isAGS: Yup.boolean(),
    agsExport: Yup.object().when("isAGS", {
      is: true,
      then: (schema) =>
        schema.shape({
          invoiceNo: Yup.string().required(
            "Número de factura AGS es obligatorio"
          ),
        }),
    }),
  }),
});

const defaultAgsExport = {
  invoiceNo: "",
  freightDescription: "",
  freightCost: "",
  defaultMarkdownPercentage: "",
};

const initialValues = {
  export: {
    companyId: null,
    consigneeId: null,
    statusId: null,
    forwarderId: null,
    packingListDate: null,
    agentDeliveryDate: null,
    requestNo: "",
    hasLock: false,
    lockNo: "",
    containerTypeId: null,
    shipmentTypeId: null,
    incotermId: null,
    invoiceNo: "",
    invoiceDate: null,
    isAGS: false,
    agsExport: defaultAgsExport,
  },
};

const touchNested = (value) => {
  if (Array.isArray(value)) {
    return value.map(touchNested);
  }
  if (value !== null && typeof value === "object") {
    return Object.keys(value).reduce((acc, key) => {
      acc[key] = touchNested(value[key]);
      return acc;
    }, {});
  }
  return true;
};

const flattenErrors = (errors) => {
  if (!errors) return [];
  if (typeof errors === "string") return [errors];
  if (Array.isArray(errors)) {
    return errors.flatMap((item) => flattenErrors(item));
  }
  if (typeof errors === "object") {
    return Object.values(errors).flatMap((value) => flattenErrors(value));
  }
  return [];
};

const ExportForm = ({ id, companyId }) => {
  const formMode = id ? "edit" : "insert";
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState(initialValues);
  const { openSnack } = useSnack();
  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getExportById(id);
      setFormData({
        export: {
          ...res.data,
          hasLock: res.data.lockNo ? true : false,
          isAGS: Boolean(res.data.agsExport),
          agsExport: res.data.agsExport
            ? {
                invoiceNo: res.data.agsExport.invoiceNo || "",
                freightDescription: res.data.agsExport.freightDescription || "",
                freightCost: res.data.agsExport.freightCost ?? "",
                defaultMarkdownPercentage:
                  res.data.agsExport.defaultMarkdownPercentage ?? "",
              }
            : defaultAgsExport,
        },
      });
    } catch (e) {
      openSnack(handleError(e), "error");
    }
    setLoading(false);
  };

  const handleSubmit = async (values) => {
    const formatDate = (date) =>
      date ? dayjs(date).format("YYYY-MM-DD") : null;

    const { isAGS, agsExport, ...exportValues } = values.export;

    const formattedValues = {
      export: {
        ...exportValues,
        companyId,
        packingListDate: formatDate(values.export.packingListDate),
        agentDeliveryDate: formatDate(values.export.agentDeliveryDate),
        invoiceDate: formatDate(values.export.invoiceDate),
        agsExport: isAGS
          ? {
              invoiceNo: agsExport.invoiceNo,
              freightDescription: agsExport.freightDescription || null,
              freightCost:
                agsExport.freightCost === "" ? null : Number(agsExport.freightCost),
              defaultMarkdownPercentage:
                agsExport.defaultMarkdownPercentage === ""
                  ? null
                  : Number(agsExport.defaultMarkdownPercentage),
            }
          : null,
      },
    };

    try {
      let res;
      if (formMode === "insert") {
        res = await createExport({ ...formattedValues, exportItems: [] });
        openSnack("Exportación guardada exitosamente", "success");
      } else {
        res = await updateExport(id, formattedValues);
        openSnack("Exportación actualizada exitosamente", "success");
      }
      // Header-only save: always continue to the granular items page, mirroring
      // the Purchase Order "materia prima" flow (PurchaseOrderForm.js).
      navigate("/exports/items", { state: { id: res.data.id ?? id } });
    } catch (e) {
      openSnack(handleError(e), "error");
    }
  };

  useEffect(() => {
    if (formMode === "edit") {
      fetchData();
    }
  }, []);

  return loading ? (
    <CircularProgress />
  ) : (
    <Formik
      enableReinitialize
      initialValues={formData}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        handleChange,
        handleBlur,
        setFieldValue,
        errors,
        touched,
        validateForm,
        setTouched,
        submitForm,
      }) => (
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h4">Datos de Exportación</Typography>
            </Grid>

            <Grid item xs={6}>
              <FastField
                name="export.consigneeId"
                label="Cliente"
                as={DropdownSelector}
                fetchFunction={getClientList}
                fetchParams={{ companyId, pageSize: 1000 }}
              />
            </Grid>

            <Grid item xs={6}>
              <FastField
                name="export.statusId"
                label="Estatus"
                as={DropdownSelector}
                fetchFunction={getStatusCatalogue}
                fetchParams={"EXPORT"}
              />
            </Grid>

            <Grid item xs={6}>
              <FastField
                name="export.forwarderId"
                label="Forwarder"
                as={DropdownSelector}
                fetchFunction={getCatalogue}
                fetchParams={{ catalogueName: "EXPORT_FORWARDER" }}
              />
            </Grid>

            <Grid item xs={6}>
              <MyDatePicker
                fullWidth
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error:
                      touched?.export?.packingListDate &&
                      Boolean(errors?.export?.packingListDate),
                    helperText:
                      touched?.export?.packingListDate &&
                      errors?.export?.packingListDate,
                  },
                }}
                label="Fecha de Packing List"
                value={
                  values.export.packingListDate
                    ? dayjs(values.export.packingListDate)
                    : null
                }
                onChange={(value) =>
                  setFieldValue("export.packingListDate", value)
                }
                onBlur={handleBlur("export.packingListDate")}
              />
            </Grid>

            <Grid item xs={6}>
              <MyDatePicker
                fullWidth
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error:
                      touched?.export?.agentDeliveryDate &&
                      Boolean(errors?.export?.agentDeliveryDate),
                    helperText:
                      touched?.export?.agentDeliveryDate &&
                      errors?.export?.agentDeliveryDate,
                  },
                }}
                label="Fecha de Entrega"
                value={
                  values.export.agentDeliveryDate
                    ? dayjs(values.export.agentDeliveryDate)
                    : null
                }
                onChange={(value) =>
                  setFieldValue("export.agentDeliveryDate", value)
                }
                onBlur={handleBlur("export.agentDeliveryDate")}
              />
            </Grid>

            <Grid item xs={6}>
              <FastField
                name="export.invoiceNo"
                label="Número de Factura"
                as={TextField}
                fullWidth
                error={
                  touched?.export?.invoiceNo &&
                  Boolean(errors?.export?.invoiceNo)
                }
                helperText={
                  touched?.export?.invoiceNo && errors?.export?.invoiceNo
                }
              />
            </Grid>

            <Grid item xs={6}>
              <MyDatePicker
                fullWidth
                slotProps={{
                  textField: {
                    fullWidth: true,
                    error:
                      touched?.export?.invoiceDate &&
                      Boolean(errors?.export?.invoiceDate),
                    helperText:
                      touched?.export?.invoiceDate &&
                      errors?.export?.invoiceDate,
                  },
                }}
                label="Fecha de Factura"
                value={
                  values.export.invoiceDate
                    ? dayjs(values.export.invoiceDate)
                    : null
                }
                onChange={(value) =>
                  setFieldValue("export.invoiceDate", value)
                }
                onBlur={handleBlur("export.invoiceDate")}
              />
            </Grid>

            <Grid item xs={4}>
              <FastField
                name="export.containerTypeId"
                label="Tipo de Contenedor"
                as={DropdownSelector}
                fetchFunction={getCatalogue}
                fetchParams={{ catalogueName: "EXPORT_CONTAINER_TYPE" }}
              />
            </Grid>

            <Grid item xs={4}>
              <FastField
                name="export.shipmentTypeId"
                label="Tipo de Embarque"
                as={DropdownSelector}
                fetchFunction={getCatalogue}
                fetchParams={{ catalogueName: "EXPORT_SHIPMENT_TYPE" }}
              />
            </Grid>

            <Grid item xs={4}>
              <FastField
                name="export.incotermId"
                label="Incoterm"
                as={DropdownSelector}
                fetchFunction={getCatalogue}
                fetchParams={{ catalogueName: "INCOTERM" }}
                optionLabel={["code", "name"]}
                optionSeparator={ " | "}
              />
            </Grid>

            <Grid item xs={12}>
              <FastField name="export.isAGS" label="Es AGS" as={LabeledCheckbox} />
            </Grid>

            {values.export.isAGS && (
              <>
                <Grid item xs={12}>
                  <Typography variant="h6">Datos AGS</Typography>
                </Grid>
                <Grid item xs={6}>
                  <FastField
                    name="export.agsExport.invoiceNo"
                    label="Número de Factura AGS"
                    as={TextField}
                    fullWidth
                    error={
                      touched?.export?.agsExport?.invoiceNo &&
                      Boolean(errors?.export?.agsExport?.invoiceNo)
                    }
                    helperText={
                      touched?.export?.agsExport?.invoiceNo &&
                      errors?.export?.agsExport?.invoiceNo
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <FastField
                    name="export.agsExport.freightDescription"
                    label="Descripción de Flete"
                    as={TextField}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={6}>
                  <FastField
                    fullWidth
                    name="export.agsExport.freightCost"
                    label="Costo de Flete"
                    component={CurrencyFieldText}
                  />
                </Grid>
                <Grid item xs={6}>
                  <FastField
                    name="export.agsExport.defaultMarkdownPercentage"
                    label="% de Descuento por Defecto"
                    as={LGNumber}
                    fullWidth
                  />
                </Grid>
              </>
            )}

            <Grid item xs={12}>
              <Box sx={{ mt: 2 }}>
                <TariffFractionForm
                  values={values}
                  handleChange={handleChange}
                  handleBlur={handleBlur}
                  touched={touched}
                  errors={errors}
                />
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Button
                color="primary"
                variant="contained"
                fullWidth
                type="button"
                onClick={async () => {
                  const validationErrors = await validateForm();
                  const messages = flattenErrors(validationErrors);
                  if (messages.length > 0) {
                    setTouched(touchNested(values), false);
                    openSnack(messages[0], "error");
                    return;
                  }
                  await submitForm();
                }}
              >
                {formMode === "edit" ? "Actualizar" : "Crear"} Exportación
              </Button>
            </Grid>
          </Grid>
        </Form>
      )}
    </Formik>
  );
};

export default ExportForm;
